import {
  ICLEPeriod,
  IComplianceGroup,
  IEvaluatedJurisdictionView,
  UserTrackerData
} from '@/@types/client-api';
import {
  useDownloadDataForAllRosterMembersMutation,
  useGetPaginatedRosterMembersQuery,
  useGetUserTrackerDataQuery
} from '@/redux/api/client/creditTrackerAdmin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { formatDateLabel } from '../../CreditTracker/util/trackerDateHelpers';
import { TFilter, TFilterItem } from '../../Filter/FilterRow';
import { FilterProps, SelectorItem } from '../../Filter/types';
import { CREDIT_TRACKER_ADMIN_FILTERS_LABEL } from '../utils/constants';
import { useSearchParams } from 'react-router-dom';

export interface UserTrackerDataRow {
  jurisdiction: string | null | undefined;
  complianceGroup: string;
  compliancePeriod: string;
  compliancePeriodWarning: string;
  totalForPeriod: string;
  totalForPeriodSmallScreen: string;
  isFulfilled: boolean | null | undefined;
  creditRegionShortDescription: string | null | undefined;
  isDeadlineSpecified?: boolean;
}

export interface CreditTrackerAdminRow {
  name: string | null | undefined;
  individualSk?: number;
  organizationSk?: number;
  userTrackerData: UserTrackerDataRow[];
}

type UseCreditTrackerAdminHookPros = {
  setupIncompleteText?: string;
  filterDescriptionText?: string;
};

type ComplianceGroupFilter = {
  creditRegion: string;
  complianceGroupSk: number;
  complianceGroupName: string;
};

const useCreditTrackerAdmin = ({
  setupIncompleteText,
  filterDescriptionText
}: UseCreditTrackerAdminHookPros) => {
  const auth = useAuth();
  const [params] = useSearchParams();
  const orgIdParam = params.get('OrgId');
  const userIdParam = params.get('UserId');

  const [userOrganization, setUserOrganization] = useState(
    userIdParam
      ? -1
      : parseInt(orgIdParam ? orgIdParam : (auth.user?.profile['OrganizationSK'] as string))
  );
  const [jurisdictionsFilter, setJurisdictionsFilter] = useState<(TFilterItem | undefined)[]>([]);
  const [complianceGroupsFilter, setComplianceGroupsFilter] = useState<ComplianceGroupFilter[]>([]);
  const [queryFilter, setQueryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const defaultLabels = {
    pageDescriptionText: 'Review and edit licenses and credit for everyone on your roster',
    downloadExcelLabel: 'DOWNLOAD ALL IN EXCEL FILE',
    emailComplianceTooltipHeader: 'Email member',
    emailComplianceTooltipBody: 'Click to email your roster member about their compliance status',
    creditTrackerIncompleteTooltipHeader: 'Credit tracker incomplete',
    creditTrackerIncompleteTooltipBody: "Click to set up member's credit tracker on their behalf",
    setupIncompleteText: 'Setup incomplete',
    filterDescriptionText: 'This is a summary of jurisdictions present for members on your roster'
  };

  const {
    data: rosterData,
    isLoading: isRosterDataLoading,
    refetch: refetchRosterData,
    isFetching: isRosterDataFetching
  } = useGetPaginatedRosterMembersQuery(
    {
      orgSk: userOrganization,
      query: queryFilter,
      indSk: userIdParam ? parseInt(userIdParam) : -1,
      page: currentPage - 1,
      jurisdictions: jurisdictionsFilter.map(x => x?.data?.value ?? ''),
      complianceGroups: complianceGroupsFilter.map(x => x.complianceGroupSk.toString())
    },
    { skip: !userOrganization }
  );

  useEffect(() => {
    if (rosterData?.organizationSk) {
      setUserOrganization(rosterData?.organizationSk);
    }
  }, [rosterData?.organizationSk]);

  const {
    data: trackerData,
    isLoading: isTrackerDataLoading,
    refetch: refetchTrackerData,
    isFetching: isTrackerDataFetching
  } = useGetUserTrackerDataQuery(
    {
      orgSk: userOrganization,
      userTrackerDataRequests:
        rosterData?.members?.map(x => ({ individualSk: x.individual_SK, flags: [] })) ?? []
    },
    { skip: !rosterData?.members || rosterData.members.length === 0 }
  );

  const goToNextPage = useCallback(() => {
    setCurrentPage((prevPage: number) => prevPage + 1);
  }, []);
  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prevPage: number) => Math.max(prevPage - 1, 0));
  }, []);

  const getComplianceGroup = (complianceGroupSk: number, complianceGroups: IComplianceGroup[]) => {
    return complianceGroups.find(c => c.sk === complianceGroupSk)?.shortDescription ?? '';
  };

  const labelCompliancePeriodFromFlag = (flag: string, cleKey: string) => {
    let compliancePeriodLabel = 'Current';
    if (flag) {
      if (flag.indexOf('#' + cleKey + '$Period$-1') >= 0) {
        compliancePeriodLabel = 'Previous';
      } else if (flag.indexOf('#' + cleKey + '$Period$1') >= 0) {
        compliancePeriodLabel = 'Next';
      }
    }

    return compliancePeriodLabel;
  };

  const getTotalForPeriod = (jurisdiction?: IEvaluatedJurisdictionView) => {
    return (jurisdiction?.clePeriod?.isUserSet || jurisdiction?.clePeriod?.hasDefaultDate) &&
      jurisdiction?.totalRequirement?.required &&
      !jurisdiction.isNonCEJurisdiction
      ? `${jurisdiction.totalRequirement?.counted}/${jurisdiction.totalRequirement?.required}`
      : '';
  };

  const refetchData = async () => {
    await refetchRosterData();
    await refetchTrackerData();
  };

  useEffect(() => {
    setJurisdictionsFilter(jurisdictionsFilterValues =>
      jurisdictionsFilterValues.map(j => {
        const label =
          rosterData?.filters?.find(x => x.creditRegion === j?.data?.value)
            ?.creditRegionLongDescription ?? j?.data?.value;
        return {
          data: {
            name: j?.data?.value,
            value: j?.data?.value ?? '',
            label: label,
            //label: `${label}: ${complianceGroupsFilter.find(x => x.creditRegion === j?.data?.value)?.complianceGroupName ?? 'All'}`,
            checked: j?.data?.checked,
            groupTypeId: CREDIT_TRACKER_ADMIN_FILTERS_LABEL.JURISDICTIONS
          },
          canDeselect: true
        };
      })
    );
  }, [complianceGroupsFilter, rosterData]);

  const jurisdictionFilterProps: FilterProps = useMemo(() => {
    const isJurisdictionFilterItemSelected = (value: string) => {
      return jurisdictionsFilter.map(x => x?.data?.value).includes(value);
    };

    const onJurisdictionFilterValueChange = (value?: string) => {
      if (isJurisdictionFilterItemSelected(value ?? '')) {
        setJurisdictionsFilter(jurisdictionsFilter.filter(x => x?.data?.value !== value));
        setComplianceGroupsFilter(complianceGroupsFilter.filter(x => x.creditRegion !== value));
      } else {
        const label =
          rosterData?.filters?.find(x => x.creditRegion === value)?.creditRegionLongDescription ??
          value;
        const newJurisdictionFilter = {
          data: {
            name: value,
            value: value ?? '',
            label: label,
            //label: `${label}: All`,
            checked: true,
            groupTypeId: CREDIT_TRACKER_ADMIN_FILTERS_LABEL.JURISDICTIONS
          },
          canDeselect: true
        };
        setJurisdictionsFilter([...jurisdictionsFilter, newJurisdictionFilter]);
      }
    };

    return {
      title: CREDIT_TRACKER_ADMIN_FILTERS_LABEL.JURISDICTIONS,
      subtitle: filterDescriptionText ?? defaultLabels.filterDescriptionText,
      items:
        rosterData?.filters?.map(
          x =>
            ({
              label: x.creditRegionLongDescription,
              value: x.creditRegion,
              checked: isJurisdictionFilterItemSelected(x.creditRegion ?? '')
            }) as SelectorItem
        ) ?? [],
      groupTypeId: CREDIT_TRACKER_ADMIN_FILTERS_LABEL.JURISDICTIONS,
      type: 'select',
      onValueChange: x => onJurisdictionFilterValueChange(x)
    };
  }, [
    complianceGroupsFilter,
    defaultLabels.filterDescriptionText,
    filterDescriptionText,
    jurisdictionsFilter,
    rosterData?.filters
  ]);

  const complianceGroupsFilterProps: FilterProps[] = useMemo(() => {
    const isComplianceGroupFilterItemSelected = (value: number) => {
      return complianceGroupsFilter.map(x => x.complianceGroupSk).includes(value);
    };

    const onComplianceGroupFilterValueChange = (value: string) => {
      const values = value.split(',');
      const complianceGroupSk = parseInt(values[0]);
      const complianceGroupName = values[1];
      const creditRegion = values[2];

      if (complianceGroupsFilter.map(x => x.creditRegion).includes(creditRegion)) {
        if (
          complianceGroupsFilter.find(x => x.creditRegion === creditRegion)?.complianceGroupSk ===
          complianceGroupSk
        ) {
          setComplianceGroupsFilter(
            complianceGroupsFilter.filter(x => x.creditRegion !== creditRegion)
          );
        } else {
          setComplianceGroupsFilter(
            complianceGroupsFilter.map(x => ({
              creditRegion: x.creditRegion,
              complianceGroupSk:
                x.creditRegion === creditRegion ? complianceGroupSk : x.complianceGroupSk,
              complianceGroupName:
                x.creditRegion === creditRegion ? complianceGroupName : x.complianceGroupName
            }))
          );
        }
      } else {
        setComplianceGroupsFilter([
          ...complianceGroupsFilter,
          {
            creditRegion: creditRegion,
            complianceGroupSk: complianceGroupSk,
            complianceGroupName: complianceGroupName
          }
        ]);
      }
    };

    const containsComplianceGroups = (creditRegion: string) => {
      return (
        (rosterData?.filters?.find(x => x.creditRegion === creditRegion)?.complianceGroups
          ?.length ?? 0) > 0
      );
    };

    return []; //Not displaying compliance groups filters for now

    return jurisdictionsFilter
      .filter(x => containsComplianceGroups(x?.data?.value ?? ''))
      .map(j => ({
        title: j?.data?.value + ' Compliance Groups',
        items:
          rosterData?.filters
            ?.find(x => x.creditRegion === j?.data?.value)
            ?.complianceGroups?.map(
              cg =>
                ({
                  label: cg.name,
                  value: `${cg.sk},${cg.name},${j?.data?.value}`,
                  checked: isComplianceGroupFilterItemSelected(cg.sk ?? 0)
                }) as SelectorItem
            ) ?? [],
        groupTypeId: j?.data?.value ?? '',
        type: 'select',
        onValueChange: x => onComplianceGroupFilterValueChange(x ?? '')
      }));
  }, [complianceGroupsFilter, jurisdictionsFilter, rosterData?.filters]);

  const allFilters: FilterProps[] = useMemo(() => {
    return [jurisdictionFilterProps, ...complianceGroupsFilterProps];
  }, [complianceGroupsFilterProps, jurisdictionFilterProps]);

  const clearAllFilters = () => {
    setJurisdictionsFilter([]);
    setComplianceGroupsFilter([]);
  };

  const onFilterToggle = (filter: TFilter) => {
    if (filter.value === 'clear-filters') {
      clearAllFilters();
    } else {
      setJurisdictionsFilter(jurisdictionsFilter.filter(x => x?.data?.value !== filter.value));
      setComplianceGroupsFilter(
        complianceGroupsFilter.filter(x => x.creditRegion !== filter.value)
      );
    }
  };

  const members = useMemo(() => {
    const setupIncomplete = setupIncompleteText ?? defaultLabels.setupIncompleteText;

    const getCompliancePeriod = (clePeriod?: ICLEPeriod) => {
      return (clePeriod?.isUserSet || clePeriod?.hasDefaultDate) && clePeriod?.endDate
        ? formatDateLabel(
            clePeriod.isUserInfoIncomplete ?? false,
            clePeriod.startDate,
            clePeriod.endDate,
            'MM/dd/yyyy'
          )
        : setupIncomplete;
    };

    const getCompliancePeriodWarning = (userTrackerData: UserTrackerData) => {
      const period =
        userTrackerData.creditRegionShortDescription &&
        labelCompliancePeriodFromFlag(
          userTrackerData.jurisdiction?.activeFlags?.filter(
            f => f.indexOf(userTrackerData.creditRegionShortDescription + '$Period') > 0
          )[0] ?? '',
          userTrackerData.creditRegionShortDescription
        );
      const delta =
        userTrackerData.jurisdiction?.clePeriod?.endDate &&
        (new Date(userTrackerData.jurisdiction.clePeriod?.endDate).getTime() -
          new Date().getTime()) /
          (24 * 60 * 60 * 1000);

      return period === 'Current' &&
        (userTrackerData.jurisdiction?.clePeriod?.isUserSet ||
          userTrackerData.jurisdiction?.clePeriod?.hasDefaultDate) &&
        userTrackerData.jurisdiction.totalRequirement &&
        !userTrackerData.jurisdiction.totalRequirement.isFulfilled &&
        delta &&
        delta >= 0 &&
        delta <= 31
        ? `${Math.floor(delta)} days remaining`
        : delta && delta < -1
          ? 'Past due'
          : '';
    };

    const getUserTrackerData = (data: UserTrackerData[]) => {
      return data.map(t => {
        const totalForPeriod = getTotalForPeriod(t.jurisdiction);
        const compliancePeriodLabel = getCompliancePeriod(t.jurisdiction?.clePeriod);

        return {
          jurisdiction: t.creditRegionLongDescription,
          complianceGroup: getComplianceGroup(t.complianceGroup_SK ?? -1, t.complianceGroups ?? []),
          compliancePeriod: compliancePeriodLabel,
          compliancePeriodWarning: getCompliancePeriodWarning(t),
          totalForPeriod: totalForPeriod || '—',
          totalForPeriodSmallScreen: totalForPeriod,
          isFulfilled:
            t.jurisdiction?.totalRequirement?.isFulfilled &&
            (totalForPeriod || setupIncomplete) !== setupIncomplete,
          creditRegionShortDescription: t.creditRegionShortDescription,
          isDeadlineSpecified: compliancePeriodLabel !== setupIncomplete
        };
      });
    };

    return rosterData?.members?.map(x => {
      let userData: UserTrackerData[] = [];
      if (trackerData) {
        userData = trackerData[x.individual_SK ?? 0] ?? [];
      }

      return {
        name: x.displayName,
        individualSk: x.individual_SK,
        organizationSk: userOrganization,
        userTrackerData: getUserTrackerData(userData)
      };
    });
  }, [
    defaultLabels.setupIncompleteText,
    rosterData?.members,
    setupIncompleteText,
    trackerData,
    userOrganization
  ]);

  const [
    downloadDataForAllRosterMembers,
    { isSuccess: isDownloadAllSuccess, isError: isDownloadAllError }
  ] = useDownloadDataForAllRosterMembersMutation();

  const downloadDataForAllRosterMembersFile = useCallback(async (): Promise<void> => {
    if (userOrganization) {
      await downloadDataForAllRosterMembers(userOrganization);
    }
  }, [downloadDataForAllRosterMembers, userOrganization]);

  const selectedFilters = useMemo(() => {
    if (jurisdictionsFilter.length > 0) {
      const clearAllFiltersButton = {
        data: {
          name: 'clear-filters',
          value: 'clear-filters',
          label: 'Clear all filters',
          checked: false,
          groupTypeId: 'clear-filters'
        },
        canDeselect: true
      };

      return [...jurisdictionsFilter, clearAllFiltersButton];
    }

    return [];
  }, [jurisdictionsFilter]);

  return {
    members,
    isRosterDataLoading: isRosterDataLoading || isRosterDataFetching,
    isTrackerDataLoading: isTrackerDataLoading || isTrackerDataFetching,
    allFilters,
    selectedFilters: selectedFilters,
    currentPage: currentPage,
    totalPages: rosterData?.totalPages ?? 0,
    totalResults: rosterData?.totalResults ?? 0,
    defaultLabels,
    refetchData,
    goToNextPage,
    goToPreviousPage,
    onFilterToggle,
    setQueryFilter,
    clearAllFilters,
    downloadDataForAllRosterMembersFile,
    isDownloadAllSuccess,
    isDownloadAllError,
    setCurrentPage
  };
};

export default useCreditTrackerAdmin;
